/**
 * Campaign performance for a segment: import a DV360 or Google Ads placement report, then see totals and a
 * per-video table with the segment score next to the delivered numbers ("score vs reality").
 * `VideoPerformanceBlock` is the compact read-only version for the video page.
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorMessage } from '@/api/client';
import {
  PERFORMANCE_REPORT_KINDS,
  type PerformanceImportOut,
  type PerformanceReportKind,
  type PerformanceSummaryParams,
  type PerformanceTotals,
  type PerformanceVideoRow,
} from '@/api/types';
import { Alert } from '@/components/arc/alert/alert';
import { Badge } from '@/components/arc/badge/badge';
import { Button } from '@/components/arc/button/button';
import { Input } from '@/components/arc/input/input';
import { Select } from '@/components/arc/select/select';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { useToastStack } from '@/components/arc/toast-stack/toast-stack';
import { fmtMoney, fmtNumber, fmtPct } from '@/lib/format';
import { useApi } from '@/module/MomentProvider';
import { useHref } from '@/module/basePath';
import styles from './PerformanceCard.module.css';

const PERFORMANCE_KEY = ['performance'] as const;

function usePerformanceSummary(params: PerformanceSummaryParams) {
  const api = useApi();
  return useQuery({
    queryKey: [...PERFORMANCE_KEY, 'summary', params] as const,
    queryFn: () => api.performance.summary(params),
  });
}

type Stat = { label: string; value: string; empty?: boolean };

function statsFor(t: PerformanceTotals): Stat[] {
  return [
    { label: 'Impressions', value: fmtNumber(t.impressions) },
    { label: 'Views', value: fmtNumber(t.views) },
    { label: 'View rate', value: t.view_rate == null ? 'n/a' : fmtPct(t.view_rate), empty: t.view_rate == null },
    { label: 'Clicks', value: fmtNumber(t.clicks) },
    { label: 'CTR', value: t.ctr == null ? 'n/a' : fmtPct(t.ctr, 2), empty: t.ctr == null },
    { label: 'Conversions', value: fmtNumber(t.conversions) },
    { label: 'Spend', value: fmtMoney(t.spend_usd, t.spend_usd >= 1000 ? 0 : 2) },  // whole dollars once it is big
    { label: 'CPV', value: t.cpv == null ? 'n/a' : fmtMoney(t.cpv, 3), empty: t.cpv == null },
  ];
}

function StatGrid({ totals }: { totals: PerformanceTotals }) {
  return (
    <dl className={styles.stats}>
      {statsFor(totals).map((s) => (
        <div key={s.label} className={styles.stat}>
          <dt className={styles.statLabel}>{s.label}</dt>
          <dd className={styles.statValue} data-empty={s.empty ? 'true' : undefined}>
            {s.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function rangeLabel(from: string | null, to: string | null): string | null {
  if (!from && !to) return null;
  if (from && to && from !== to) return `${from} to ${to}`;
  return from ?? to;
}

type VideoRow = Record<string, unknown> & {
  key: string;
  score: number | null;
  title: string;
  impressions: number;
  views: number;
  view_rate: number | null;
  clicks: number;
  ctr: number | null;
  conversions: number;
  spend_usd: number;
  cpv: number | null;
  r: PerformanceVideoRow;
};

function ImportForm({ segmentId, onImported }: { segmentId: string; onImported?: (r: PerformanceImportOut) => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const { toast } = useToastStack();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<PerformanceReportKind>('auto');
  const [campaign, setCampaign] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PerformanceImportOut | null>(null);

  const importMut = useMutation({
    mutationFn: () =>
      api.performance.importReport({
        file: file!,
        kind,
        campaign_ref: campaign.trim() || undefined,
        segment_id: segmentId,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      void qc.invalidateQueries({ queryKey: PERFORMANCE_KEY });
      toast({
        type: 'success',
        title: 'Report imported',
        description: `${fmtNumber(r.rows)} rows, ${fmtNumber(r.matched)} matched to videos in the library.`,
      });
      onImported?.(r);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <div className={styles.importBox}>
      <div className={styles.importHead}>
        <span className={styles.importLabel}>Import a campaign report</span>
        <span className={styles.importDesc}>
          Placement report from DV360 or Google Ads (CSV or TSV). Rows are joined to this segment&apos;s videos;
          importing the same report again updates the numbers instead of doubling them.
        </span>
      </div>
      {error ? (
        <Alert tone="danger" title="We could not import the report" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {result ? (
        <Alert
          tone={result.unmatched_count ? 'warning' : 'success'}
          title={`Imported ${fmtNumber(result.rows)} rows for ${result.campaign_ref || 'the campaign'}`}
          onDismiss={() => setResult(null)}
        >
          {fmtNumber(result.matched)} matched a video in the library
          {result.unmatched_count
            ? `, ${fmtNumber(result.unmatched_count)} placements are not in the library and stay unmatched.`
            : '.'}
          {result.unmatched.length ? (
            <p className={styles.unmatchedList}>Unmatched: {result.unmatched.join(', ')}</p>
          ) : null}
        </Alert>
      ) : null}
      <div className={styles.fileRow}>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
          className="visually-hidden"
          aria-label="Report file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
          <Upload size={16} strokeWidth={1.75} aria-hidden="true" />
          Choose report file
        </Button>
        <span className={styles.fileName}>{file ? file.name : 'No file chosen'}</span>
      </div>
      <div className={styles.fields}>
        <Select
          label="Report format"
          value={kind}
          onValueChange={(v) => setKind(v as PerformanceReportKind)}
          options={PERFORMANCE_REPORT_KINDS}
        />
        <Input
          label="Campaign name"
          value={campaign}
          onChange={(e) => setCampaign(e.target.value)}
          placeholder="Line item or campaign name"
          description="Optional. Defaults to the line item or campaign column in the report."
        />
        <Input
          label="From"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          description="Optional date range."
        />
        <Input
          label="To"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          description="Used as the date for reports without a date column."
        />
      </div>
      <div className={styles.actions}>
        <Button type="button" disabled={!file} loading={importMut.isPending} onClick={() => importMut.mutate()}>
          Import report
        </Button>
      </div>
    </div>
  );
}

export function PerformanceCard({ segmentId }: { segmentId: string }) {
  const href = useHref();
  const summary = usePerformanceSummary({ segment_id: segmentId });
  const data = summary.data;

  const rows = useMemo<VideoRow[]>(
    () =>
      (data?.videos ?? []).map((r) => ({
        key: `${r.video_id ?? 'x'}-${r.external_id}`,
        score: r.score == null ? null : Math.round(r.score * 100),
        title: r.title ?? r.external_id,
        impressions: r.impressions,
        views: r.views,
        view_rate: r.view_rate,
        clicks: r.clicks,
        ctr: r.ctr,
        conversions: r.conversions,
        spend_usd: r.spend_usd,
        cpv: r.cpv,
        r,
      })),
    [data],
  );

  const pct = (digits: number) => (v: unknown) =>
    v == null ? <span className="text-muted">n/a</span> : <span className={styles.num}>{fmtPct(v as number, digits)}</span>;
  const num = (v: unknown) => <span className={styles.num}>{fmtNumber(v as number)}</span>;

  const columns: DataColumn<VideoRow>[] = [
    {
      key: 'score',
      label: 'Score',
      numeric: true,
      width: 64,
      render: (v) =>
        v == null ? <span className="text-muted">n/a</span> : <span className={styles.score}>{String(v)}%</span>,
    },
    {
      key: 'title',
      label: 'Video',
      width: 220,
      render: (_v, row) => (
        <span className={styles.videoCell}>
          {row.r.video_id ? (
            <Link to={href(`/videos/${row.r.video_id}`)} className={styles.videoTitle}>
              {row.title}
            </Link>
          ) : (
            <span className={styles.videoTitle}>{row.title}</span>
          )}
          <span className={styles.videoSub}>
            {row.r.video_id ? (row.r.channel_name ?? row.r.external_id) : 'Not in the library'}
          </span>
        </span>
      ),
    },
    { key: 'impressions', label: 'Impressions', numeric: true, width: 100, render: num },
    { key: 'views', label: 'Views', numeric: true, width: 84, render: num },
    { key: 'view_rate', label: 'View rate', numeric: true, width: 88, render: pct(1) },
    { key: 'clicks', label: 'Clicks', numeric: true, width: 72, render: num },
    { key: 'ctr', label: 'CTR', numeric: true, width: 72, render: pct(2) },
    { key: 'conversions', label: 'Conv.', numeric: true, width: 72, render: num },
    {
      key: 'spend_usd',
      label: 'Spend',
      numeric: true,
      width: 88,
      render: (v) => <span className={styles.num}>{fmtMoney(v as number, (v as number) >= 1000 ? 0 : 2)}</span>,
    },
    {
      key: 'cpv',
      label: 'CPV',
      numeric: true,
      width: 76,
      render: (v) =>
        v == null ? <span className="text-muted">n/a</span> : <span className={styles.num}>{fmtMoney(v as number, 3)}</span>,
    },
  ];

  const range = data ? rangeLabel(data.date_from, data.date_to) : null;

  return (
    <section className="stack-4" aria-labelledby="performance-heading" aria-busy={summary.isLoading || undefined}>
      <div className={styles.sectionHead}>
        <h2 id="performance-heading" className={styles.sectionTitle}>
          Performance
        </h2>
        {data && data.totals.rows > 0 ? (
          <span className="text-muted text-xs">
            {data.campaigns.length} {data.campaigns.length === 1 ? 'campaign' : 'campaigns'}
            {range ? `, ${range}` : ''}
          </span>
        ) : null}
      </div>
      <ImportForm segmentId={segmentId} />
      {summary.isError ? (
        <div className="stack-4">
          <Alert tone="danger" title="We could not load performance">
            {errorMessage(summary.error)}
          </Alert>
          <div>
            <Button type="button" variant="secondary" onClick={() => void summary.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : summary.isLoading ? (
        <Skeleton label="Loading performance" lines={3} />
      ) : data && data.totals.rows > 0 ? (
        <>
          <StatGrid totals={data.totals} />
          {data.campaigns.length ? (
            <div className="row-2">
              {data.campaigns.map((c) => (
                <Badge key={c} size="sm">
                  {c || 'Unnamed campaign'}
                </Badge>
              ))}
            </div>
          ) : null}
          <div className="table-scroll">
            <SortableDataTable
              rows={rows}
              columns={columns}
              rowKey="key"
              caption="Performance per video"
              emptyMessage="No per-video rows yet."
              defaultSort={{ key: 'views', direction: 'desc' }}
              itemName={{ one: 'video', other: 'videos' }}
            />
          </div>
        </>
      ) : (
        <p className="text-muted">
          No results yet. After a campaign runs on this segment, import its placement report to see which videos
          delivered.
        </p>
      )}
    </section>
  );
}

/** Compact totals for one video. Renders nothing while loading or when no campaign rows exist for the video. */
export function VideoPerformanceBlock({ videoId }: { videoId: string }) {
  const summary = usePerformanceSummary({ video_id: videoId });
  const data = summary.data;
  if (!data || data.totals.rows === 0) return null;
  const range = rangeLabel(data.date_from, data.date_to);
  return (
    <section className={styles.compact} aria-labelledby="video-performance-heading">
      <div className={styles.compactHead}>
        <h2 id="video-performance-heading" className={styles.compactTitle}>
          Performance
        </h2>
        <span className="text-muted text-xs">
          {data.campaigns.filter(Boolean).join(', ') || 'Imported campaign results'}
          {range ? `, ${range}` : ''}
        </span>
      </div>
      <StatGrid totals={data.totals} />
    </section>
  );
}
